import { useI18n } from '@ecom/i18n';
import {
  EmptyState,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@ecom/ui-kit';
import { type ReactElement } from 'react';
import { Link } from 'react-router-dom';

import { type DashboardKpis, type ToHandleReason } from '../types';

import { MerchantStatusBadge } from '@/features/platform-admin/components/MerchantStatusBadge';
import { MERCHANT_STATUSES, type MerchantStatus } from '@/features/platform-admin/types';

/** Where each reason is worked on: the merchants list, already narrowed to what the reason is about. */
const DESTINATION: Readonly<Record<ToHandleReason, string>> = {
  unassigned: '/merchants?scope=unassigned',
  pending_over_7d: '/merchants?status=pending',
  provisional_expired: '/merchants',
};

const REASON_KEY = {
  unassigned: 'admin.dashboard.act.reason.unassigned',
  provisional_expired: 'admin.dashboard.act.reason.provisional_expired',
  pending_over_7d: 'admin.dashboard.act.reason.pending_over_7d',
} as const;

const asStatus = (value: string): MerchantStatus | null =>
  MERCHANT_STATUSES.find((status) => status === value) ?? null;

/** The shops that need a decision, strongest reason first (the server's own order), each with where to do it. */
export function ToHandleTable({
  toHandle,
}: {
  readonly toHandle: DashboardKpis['toHandle'];
}): ReactElement {
  const { t } = useI18n();
  if (toHandle.items.length === 0)
    return <EmptyState title={t('admin.dashboard.act.noneToHandle')} />;

  const title = t('admin.dashboard.act.toHandleTitle');
  return (
    <div className="flex flex-col gap-2" data-testid="dashboard-to-handle">
      <Table caption={title} captionVisible>
        <TableHead>
          <TableRow>
            <TableHeaderCell>{t('admin.dashboard.act.colShop')}</TableHeaderCell>
            <TableHeaderCell>{t('admin.dashboard.act.colStatus')}</TableHeaderCell>
            <TableHeaderCell>{t('admin.dashboard.act.colReason')}</TableHeaderCell>
            <TableHeaderCell align="end">{t('admin.dashboard.act.colAge')}</TableHeaderCell>
            <TableHeaderCell>{t('admin.dashboard.act.colManager')}</TableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {toHandle.items.map((item) => {
            const status = asStatus(item.status);
            return (
              <TableRow key={item.tenantId}>
                <TableCell>
                  <Link
                    to={DESTINATION[item.reason]}
                    className="font-medium text-accent hover:underline"
                  >
                    {item.name ?? t('admin.dashboard.act.unnamed')}
                  </Link>
                </TableCell>
                <TableCell>
                  {status === null ? item.status : <MerchantStatusBadge status={status} />}
                </TableCell>
                <TableCell>{t(REASON_KEY[item.reason])}</TableCell>
                <TableCell align="end">
                  {t('admin.dashboard.act.ageDays', { days: item.ageDays })}
                </TableCell>
                <TableCell>{item.managerEmail ?? t('admin.dashboard.act.noManager')}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <p className="text-xs text-inkFaint">
        {t('admin.dashboard.act.toHandleTotal', {
          shown: toHandle.items.length,
          total: toHandle.total,
        })}
      </p>
    </div>
  );
}
